import { useState } from 'react';
import type { DowngradeChoices, DowngradePreview } from '@icetus/contracts';
import { Badge, Button } from '@icetus/ui';
import { choiceProblems, defaultChoices, keepLimit, toggleLocale, toggleMember, toggleSite } from '../lib/downgrade-choice';
import { LocaleFlag } from './locale-flag';
import { Modal } from './overlay';

/**
 * 降档选择页。
 *
 * 它存在的理由只有一句：**降档要收回的东西必须客户当场点头**。
 * 以前点"下周期降为这一档"只是登记一个套餐代码，到期那天扫描器按
 * "留最早建的"把另外两个站静默停掉 —— 客户是第二天发现网站没了才知道的。
 *
 * 三条界面上的取舍：
 *   · 每一项都给"现在几个 / 新档几个"，只给上限等于让客户自己数；
 *   · 不能选的东西（默认语言、owner、自己）显示成灰的并写明理由，
 *     比让他勾上再报错好 —— 报错时他会以为是我们坏了；
 *   · 底部永远留一条"算了，我都想保留，按原套餐续费"。这是给"看到代价后
 *     反悔"的人准备的路，而不是让他关掉弹窗再自己去找续费按钮。
 */
export function DowngradeDialog({
  preview,
  busy,
  onClose,
  onConfirm,
  onKeepCurrent,
}: {
  preview: DowngradePreview;
  busy: boolean;
  onClose: () => void;
  onConfirm: (choices: DowngradeChoices) => void;
  /** 「算了，按原套餐续费」：撤销降档并直接按其当前套餐下续费单 */
  onKeepCurrent: () => void;
}) {
  const [choices, setChoices] = useState<DowngradeChoices>(() => defaultChoices(preview));
  const problems = choiceProblems(preview, choices);

  const siteMax = keepLimit(preview.sites.limit, preview.sites.items.length);
  const seatMax = keepLimit(preview.members.limit, preview.members.items.length);

  return (
    <Modal
      open
      size="lg"
      onClose={onClose}
      title={`降为${preview.to.nameZh}，这些要收回来`}
      description={`下个周期开始按${preview.to.nameZh}计费。下面的选择现在就能改，到期那天按你选的执行；没勾的站点会下线（内容都在，随时能开回来）。`}
      footer={
        <>
          <Button variant="ghost" disabled={busy} onClick={onKeepCurrent}>
            算了，我都想保留，按原套餐续费
          </Button>
          <span className="overlay__spacer" />
          <Button disabled={busy} onClick={onClose}>
            取消
          </Button>
          <Button
            variant="primary"
            disabled={busy || problems.length > 0}
            onClick={() => onConfirm(choices)}
          >
            {busy ? '提交中…' : '确认，下周期生效'}
          </Button>
        </>
      }
    >
      {problems.length > 0 ? (
        <ul className="dg-problems">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}

      {/* ── 站点 ── */}
      <section className="dg-sec">
        <header className="dg-sec__head">
          <h3>站点</h3>
          <span className="muted">
            {preview.from.quotas.sites} 个 → {preview.to.quotas.sites} 个 · 已保留{' '}
            {choices.sites.length} / 最多 {siteMax}
          </span>
        </header>
        <ul className="dg-list">
          {preview.sites.items.map((site) => {
            const checked = choices.sites.includes(site.id);
            // 到上限后不许再加，但允许先取消再换另一个
            const blocked = !checked && choices.sites.length >= siteMax;
            return (
              <li key={site.id}>
                <label className={`dg-row${blocked ? ' is-disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={busy || blocked}
                    onChange={() => setChoices((c) => toggleSite(c, site.id))}
                  />
                  <span className="dg-row__main">
                    <span className="dg-row__title">{site.name}</span>
                    <span className="dg-row__sub">{site.host ?? '未绑定域名'}</span>
                  </span>
                  {site.status === 'suspended' ? <Badge tone="danger">已暂停</Badge> : null}
                  {!checked ? <Badge tone="warn">将下线</Badge> : null}
                </label>
              </li>
            );
          })}
        </ul>
      </section>

      {/* ── 语言 ── */}
      <section className="dg-sec">
        <header className="dg-sec__head">
          <h3>语言</h3>
          <span className="muted">
            每站 {preview.from.quotas.locales} 种 → {preview.to.quotas.locales} 种
          </span>
        </header>
        {preview.locales.groups.length === 0 ? (
          <p className="muted">没有开启多语言的站点。</p>
        ) : (
          preview.locales.groups.map((group) => {
            const picked = choices.locales[group.siteId] ?? [];
            const over = group.limit > 0 && group.items.length > group.limit;
            if (!over) {
              return (
                <p key={group.siteId} className="muted dg-note">
                  「{group.siteName}」现在只有 {group.items.length} 种语言，在新档额度内，不用动。
                </p>
              );
            }
            const atMax = group.limit > 0 && picked.length >= group.limit;
            return (
              <div key={group.siteId} className="dg-group">
                <p className="dg-group__title">
                  {group.siteName}
                  <span className="muted">
                    {' '}
                    保留 {picked.length} / 最多 {group.limit}
                  </span>
                </p>
                <ul className="dg-chips">
                  {group.items.map((item) => {
                    const on = picked.includes(item.locale);
                    // 默认语言是整站入口，关掉它客户所有宣传物料上的地址一起失效
                    const blocked = item.isDefault || (!on && atMax);
                    return (
                      <li key={item.locale}>
                        <label className={`dg-chip${on ? ' is-on' : ''}${blocked ? ' is-disabled' : ''}`}>
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={busy || blocked}
                            onChange={() =>
                              setChoices((c) => toggleLocale(c, group.siteId, item.locale))
                            }
                          />
                          <LocaleFlag locale={item.locale} />
                          {item.locale.toUpperCase()}
                          <span className="dg-chip__pages">{item.pages} 页</span>
                          {item.isDefault ? <span className="dg-chip__tag">默认</span> : null}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })
        )}
      </section>

      {/* ── 成员 ── */}
      <section className="dg-sec">
        <header className="dg-sec__head">
          <h3>成员</h3>
          <span className="muted">
            {preview.from.quotas.seats} 人 → {preview.to.quotas.seats} 人 · 保留{' '}
            {preview.members.items.length - choices.members.length} / 最多 {seatMax}
          </span>
        </header>
        {preview.members.items.length <= seatMax ? (
          <p className="muted dg-note">
            现在 {preview.members.items.length} 个人，在新档席位内，不用动。
          </p>
        ) : (
          <ul className="dg-list">
            {preview.members.items.map((m) => {
              const removing = choices.members.includes(m.userId);
              const locked = m.role === 'owner' || m.isSelf;
              return (
                <li key={m.userId}>
                  <label className={`dg-row${locked ? ' is-disabled' : ''}`}>
                    <input
                      type="checkbox"
                      checked={removing}
                      disabled={busy || locked}
                      onChange={() => setChoices((c) => toggleMember(c, m.userId))}
                    />
                    <span className="dg-row__main">
                      <span className="dg-row__title">{m.displayName ?? m.email.split('@')[0]}</span>
                      <span className="dg-row__sub">
                        {m.email} · {m.role}
                        {m.isSelf ? ' · 你自己' : ''}
                      </span>
                    </span>
                    {removing ? <Badge tone="danger">将移出团队</Badge> : null}
                    {locked ? <span className="dg-row__lock">不能移除</span> : null}
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ── 不用选的：商品与存储 ── */}
      <section className="dg-sec">
        <header className="dg-sec__head">
          <h3>商品与存储</h3>
          <span className="muted">存量保留，只拦新增</span>
        </header>
        {preview.products.used > preview.products.limit ? (
          <p className="dg-note">
            商品现在 {preview.products.used.toLocaleString('zh-CN')} 个，新档上限{' '}
            {preview.products.limit.toLocaleString('zh-CN')} 个：现有的都留在站上，但
            <strong>暂时加不了新的</strong>。把不用的商品归档、或把整个站点归档，都能腾出位置。
          </p>
        ) : (
          <p className="dg-note">
            商品现在 {preview.products.used.toLocaleString('zh-CN')} 个，新档上限{' '}
            {preview.products.limit.toLocaleString('zh-CN')} 个，还有空间。
          </p>
        )}
        {preview.storage.usedMb > preview.storage.limitMb ? (
          <p className="dg-note">
            存储现在 {preview.storage.usedMb.toLocaleString('zh-CN')} MB，新档上限{' '}
            {preview.storage.limitMb.toLocaleString('zh-CN')} MB：已上传的图片全部保留，但
            <strong>暂时不能传新图</strong>，删掉不用的图片即可。
          </p>
        ) : (
          <p className="dg-note">
            存储现在 {preview.storage.usedMb.toLocaleString('zh-CN')} MB，新档上限{' '}
            {preview.storage.limitMb.toLocaleString('zh-CN')} MB，还有空间。
          </p>
        )}
      </section>
    </Modal>
  );
}
